import { wait } from '../../../../helpers/jsHelper';
import { toUnityFixed } from '../../../../helpers/unityHelper';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';
import { selectSelectedHeight } from '../../../../redux/slicers/productBuilder/productBuilder.slicer';
import { getAppState } from '../../../../redux/store';
import { callUnityEventModifyProperty, enableGameObject, disableGameObject } from '../ProductBuilderComponents/commonHelper';
import { undoRedo } from './UndoRedo';
export class UndoRedoProductBuilder {
  constructor({ undoRedoInstance }) {
    this.undoRedo = undoRedoInstance;
    this.pendingChanges = new Map();
    this.createAction = this.undoRedo.createAction;
    this._initActions();
  }

  _initActions() {
    this._pushPropertyChange = this.createAction(
      ({
        prevValue,
        nextValue,
        unityPropertyName,
        objectName,
        sideLocation,
        componentType,
        updateCallback,
        index,
      }) => {
        if (prevValue === nextValue) return false;
        // Map React state key → Unity transformParamaterType when they differ
        const unityTypeMap = {
          labelHandleDepthValue: 'modifyLabelHandleDepth',
        };
        const unityTransformType = unityTypeMap[unityPropertyName] || unityPropertyName;
        const sendToUnity = (val) => {
          callUnityEventModifyProperty(
            val,
            unityTransformType,
            objectName,
            sideLocation,
            componentType,
            false,
            true
          );
        };
        return {
          __ACTION__: 'PRODUCT_BUILDER_PROP_CHANGE',
          undo: () => {
            sendToUnity(prevValue);
            setTimeout(() => {
              updateCallback?.(index, unityPropertyName, prevValue);
            }, 10);
          },
          redo: () => {
            sendToUnity(nextValue);
            setTimeout(() => {
              updateCallback?.(index, unityPropertyName, nextValue);
            }, 10);
          },
        };
      }
    );

    this._pushMirrorAction = this.createAction(
      ({ objectName, componentType, index, undoCallback, redoCallback }) => {
        return {
          __ACTION__: 'PRODUCT_BUILDER_MIRROR_ACTION',
          undo: async () => {
            await undoCallback?.();
          },
          redo: async () => {
            await redoCallback?.();
          },
        };
      }
    );

    this._pushAdjacentAction = this.createAction(
      ({ objectName, componentType, index, undoCallback, redoCallback }) => {
        return {
          __ACTION__: 'PRODUCT_BUILDER_ADJACENT_ACTION',
          undo: async () => {
            await undoCallback?.();
          },
          redo: async () => {
            await redoCallback?.();
          },
        };
      }
    );

    // Add/Remove label/handle action
    this._pushLabelHandleAddRemove = this.createAction(
      ({ objectName, componentType, refreshCallback }) => {
        if (!objectName) return false;
        const selectedHeight = selectSelectedHeight(getAppState());
        return {
          __ACTION__: 'PRODUCT_BUILDER_LABEL_HANDLE_ADD_REMOVE',
          undo: async () => {
            disableGameObject({
              propInfoCommandDatas: [{ objectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            enableGameObject({
              propInfoCommandDatas: [{ objectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      }
    );

    // Delete label/handle action (opposite of add)
    this._pushLabelHandleDelete = this.createAction(
      ({ objectName, componentType, refreshCallback }) => {
        if (!objectName) return false;
        const selectedHeight = selectSelectedHeight(getAppState());
        return {
          __ACTION__: 'PRODUCT_BUILDER_LABEL_HANDLE_DELETE',
          undo: async () => {
            // Undo delete = restore the label/handle (enable)
            enableGameObject({
              propInfoCommandDatas: [{ objectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            // Redo delete = delete again (disable)
            disableGameObject({
              propInfoCommandDatas: [{ objectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            refreshCallback?.() 
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);  
          },
        };
      }
    );

    // Replace label/handle action (disable old, enable new)
    this._pushLabelHandleReplace = this.createAction(
      ({ oldObjectName, newObjectName, componentType, refreshCallback }) => {
        if (!oldObjectName || !newObjectName) return false;
        const selectedHeight = selectSelectedHeight(getAppState());
        return {
          __ACTION__: 'PRODUCT_BUILDER_LABEL_HANDLE_REPLACE',
          undo: async () => {
            // First: Disable the new label/handle
            disableGameObject({
              propInfoCommandDatas: [{ objectName: newObjectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 50));
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            

            // Second: Enable the old label/handle
            enableGameObject({
              propInfoCommandDatas: [{ objectName: oldObjectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            

            // Refresh after both operations
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
          redo: async () => {
            // First: Disable the old label/handle
            disableGameObject({
              propInfoCommandDatas: [{ objectName: oldObjectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });

            // Small delay to ensure Unity processes
            // await new Promise((resolve) => setTimeout(resolve, 50));
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            

            // Second: Enable the new label/handle
            enableGameObject({
              propInfoCommandDatas: [{ objectName: newObjectName }],
              productBuilderModel: {height: selectedHeight?.value || "12"},
            });
            await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            

            // Refresh after both operations
            refreshCallback?.()
            await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
          },
        };
      }
    );

    // Delete multiple objects action (handles multiple deletions as one action)
    this._pushMultiDelete = this.createAction(({ objectsData, refreshCallback, topAndBottomObjectAvailable, filterTuftData, filteredObjectNames }) => {
      const selectedHeight = selectSelectedHeight(getAppState());
      if (!objectsData || objectsData.length === 0) return false;
      const isTuftDataAvailable = filterTuftData?.finalButtonConfigData?.length > 0;

      return {
        __ACTION__: 'PRODUCT_BUILDER_MULTI_DELETE',
        undo: async () => {
          // Undo delete = restore all deleted objects (enable)
          const propInfoCommandDatas = filteredObjectNames?.length ? filteredObjectNames : objectsData.map(({ objectName }) => ({ objectName }));
          // Pass all objects in a single array
          enableGameObject({
            propInfoCommandDatas,
            productBuilderModel: { 
              height: selectedHeight?.value || "12",
              operationType:"Delete",
              ...(topAndBottomObjectAvailable ? { internalLayerView:filterTuftData?.internalLayerView || "Exploded" , valueForView:filterTuftData?.valueForView || 0 } : {}),
              ...(filterTuftData?.internalLayerPayload ? { arrangeLayers:true } : {}),
              ...(filterTuftData?.internalLayerPayload ? { ...filterTuftData?.internalLayerPayload } : {})
            },
            ...(isTuftDataAvailable ? { tuftedButtonConfigurations:filterTuftData?.finalButtonConfigData, tuftedButtonConfiguration:null } : {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
          setTimeout(() => refreshCallback?.(), 10);
        },
        redo: async () => {
          // Redo delete = delete all again (disable)
          // Pass all objects in a single array
          const propInfoCommandDatas = objectsData.map(({ objectName }) => ({ objectName }));
          disableGameObject({
            propInfoCommandDatas,
            productBuilderModel: { 
              height: selectedHeight?.value || "12", 
              operationType:"Delete",
              ...(topAndBottomObjectAvailable ? { internalLayerView:filterTuftData?.internalLayerView || "Exploded" , valueForView:filterTuftData?.valueForView || 0 } : {}),
              ...(filterTuftData?.internalLayerPayload ? { arrangeLayers:true } : {}),
              ...(filterTuftData?.internalLayerPayload ? { ...filterTuftData?.internalLayerPayload } : {})
            }
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
          setTimeout(() => refreshCallback?.(), 10);
        },
      };
    });

    // Add this new action for layer type changes
    this._pushLayerTypeChange = this.createAction(
      ({ prevLayerType, nextLayerType, gap, applyLayerChange }) => {
        if (prevLayerType === nextLayerType) return false;
        return {
          __ACTION__: 'PRODUCT_BUILDER_LAYER_TYPE_CHANGE',
          undo: async () => {
            await applyLayerChange(prevLayerType, false);
          },
          redo: async () => {
            await applyLayerChange(nextLayerType, false);
          },
        };
      }
    );

    // Add this new action for sidebar section changes
    this._pushSidebarSectionChange = this.createAction(
      ({ prevSection, nextSection, prevLayerType, nextLayerType, applySectionChange }) => {
        // Don't track if both section and layer type are the same
        if (prevSection === nextSection && prevLayerType === nextLayerType) return false;
        return {
          __ACTION__: 'PRODUCT_BUILDER_SIDEBAR_SECTION_CHANGE',
          undo: async () => {
            await applySectionChange(prevSection, prevLayerType, false);
          },
          redo: async () => {
            await applySectionChange(nextSection, nextLayerType, false);
          },
        };
      }
    );

    // Update this combined action for layer switch with sidebar section
    this._pushLayerSwitchWithSection = this.createAction(
      ({ prevLayerType, nextLayerType, prevSidebarSection, nextSidebarSection, applyLayerChange, applySectionChange }) => {
        if (prevLayerType === nextLayerType) return false;
        return {
          __ACTION__: 'PRODUCT_BUILDER_LAYER_SWITCH_WITH_SECTION',
          undo: async () => {
            
            // Set skip flag BEFORE anything changes
            window.dispatchEvent(
              new CustomEvent('updateSidebarMenuRefs', {
                detail: {
                  layerType: prevLayerType,
                  defaultSection: prevSidebarSection,
                  skipLayerTypeEffect: true,
                  beforeLayerChange: true,
                },
              })
            );
            
            // Wait for skip flag to be set
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Apply layer change
            await applyLayerChange(prevLayerType, false);
            
            // Wait for layer type to be fully updated
            await new Promise(resolve => setTimeout(resolve, 150));
            
            // Apply section change (which uses flushSync internally)
            await applySectionChange(prevSidebarSection, prevLayerType);
            
          },
          redo: async () => {
            
            // Set skip flag BEFORE anything changes
            window.dispatchEvent(
              new CustomEvent('updateSidebarMenuRefs', {
                detail: {
                  layerType: nextLayerType,
                  defaultSection: nextSidebarSection,
                  skipLayerTypeEffect: true,
                  beforeLayerChange: true,
                },
              })
            );
            
            // Wait for skip flag to be set
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Apply layer change
            await applyLayerChange(nextLayerType, false);
            
            // Wait for layer type to be fully updated
            await new Promise(resolve => setTimeout(resolve, 150));
            
            // Apply section change (which uses flushSync internally)
            await applySectionChange(nextSidebarSection, nextLayerType);
            
            console.log('[UndoRedo] REDO layer switch COMPLETE');
          },
        };
      }
    );

    // Simplified layer type change with sidebar tracking
    this._pushLayerTypeChangeWithSidebar = this.createAction(
      ({ prevLayerType, nextLayerType, prevSidebarSection, applyLayerChange }) => {
        if (prevLayerType === nextLayerType) return false;
        return {
          __ACTION__: 'PRODUCT_BUILDER_LAYER_TYPE_WITH_SIDEBAR',
          undo: async () => {
            await applyLayerChange(prevLayerType, false);
            // prevSidebarSection will be restored automatically by the layerType useEffect
          },
          redo: async () => {
            await applyLayerChange(nextLayerType, false);
            // Default section will be set automatically by the layerType useEffect
          },
        };
      }
    );
  }

  startPropertyChange({ objectName, unityPropertyName, initialValue, index }) {
    const key = `${objectName}__${unityPropertyName}`;
    const storedValue = toUnityFixed(initialValue, 3);
    this.pendingChanges.set(key, {
      objectName,
      unityPropertyName,
      initialValue: storedValue,
      lastValue: storedValue,
      index,
      startedAt: Date.now(),
    });
  }

  updatePendingChange({ objectName, unityPropertyName, currentValue }) {
    const key = `${objectName}__${unityPropertyName}`;
    const item = this.pendingChanges.get(key);
    if (!item) return;
    item.lastValue = toUnityFixed(currentValue, 3);
    this.pendingChanges.set(key, item);
  }

  endPropertyChange({
    objectName,
    unityPropertyName,
    finalValue,
    sideLocation,
    componentType,
    updateCallback,
    index,
  }) {
    const key = `${objectName}__${unityPropertyName}`;
    const item = this.pendingChanges.get(key);
    if (!item) return;
    const prevValue = item.initialValue;
    const nextValue = toUnityFixed(finalValue, 3);
    this._pushPropertyChange({
      prevValue,
      nextValue,
      unityPropertyName,
      objectName,
      sideLocation,
      componentType: componentType.charAt(0).toUpperCase() + componentType.slice(1),
      updateCallback,
      index,
    });
    this.pendingChanges.delete(key);
  }

  cancelPropertyChange({ objectName, unityPropertyName }) {
    const key = `${objectName}__${unityPropertyName}`;
    this.pendingChanges.delete(key);
  }

  clearAllPendingChanges() {
    this.pendingChanges.clear();
  }

  addSideChangeAction({
    index,
    objectName,
    prevSideValue,
    nextSideValue,
    componentType,
    performSideChange,
  }) {
    // discrete action, push immediately
    this.createAction(() => ({
      __ACTION__: 'PRODUCT_BUILDER_SIDE_CHANGE',
      undo: async () => {
        await performSideChange(prevSideValue, true);
      },
      redo: async () => {
        await performSideChange(nextSideValue, true);
      },
    }))();
  }

  addMirrorAction({ objectName, componentType, index, undoCallback, redoCallback }) {
    // Use the same pattern as addSideChangeAction - immediately execute
    this.createAction(() => ({
      __ACTION__: 'PRODUCT_BUILDER_MIRROR_ACTION',
      undo: async () => {
        await undoCallback?.();
        await wait(10);
      },
      redo: async () => {
        await redoCallback?.();
        await wait(10);
      },
    }))(); // <-- IMPORTANT: Call it immediately with ()
  }

  addAdjacentAction({ objectName, componentType, index, undoCallback, redoCallback }) {
    // Use the same pattern as addSideChangeAction - immediately execute
    this.createAction(() => ({
      __ACTION__: 'PRODUCT_BUILDER_ADJACENT_ACTION',
      undo: async () => {
        await undoCallback?.();
        await wait(10);
      },
      redo: async () => {
        await redoCallback?.();
        await wait(10);
      },
    }))(); // <-- IMPORTANT: Call it immediately with ()
  }

  addLabelHandleAddAction({ objectName, componentType, refreshCallback }) {
    this._pushLabelHandleAddRemove({
      objectName,
      componentType,
      refreshCallback,
    });
    undoRedo.pause();
  }

  addLabelHandleDeleteAction({ objectName, componentType, refreshCallback }) {
    this._pushLabelHandleDelete({
      objectName,
      componentType,
      refreshCallback,
    });
  }

  addLabelHandleReplaceAction({ oldObjectName, newObjectName, componentType, refreshCallback }) {
    this._pushLabelHandleReplace({
      oldObjectName,
      newObjectName,
      componentType,
      refreshCallback,
    });
  }

  addLayerTypeChangeAction({ prevLayerType, nextLayerType, gap, applyLayerChange }) {
    this._pushLayerTypeChange({
      prevLayerType,
      nextLayerType,
      gap,
      applyLayerChange,
    });
  }

  addSidebarSectionChangeAction({ prevSection, nextSection, prevLayerType, nextLayerType, applySectionChange }) {
    this._pushSidebarSectionChange({
      prevSection,
      nextSection,
      prevLayerType,
      nextLayerType,
      applySectionChange,
    });
  }

  addLayerSwitchWithSectionAction({ 
    prevLayerType, 
    nextLayerType, 
    prevSidebarSection, 
    nextSidebarSection,
    applyLayerChange,
    applySectionChange 
  }) {
    this._pushLayerSwitchWithSection({
      prevLayerType,
      nextLayerType,
      prevSidebarSection,
      nextSidebarSection,
      applyLayerChange,
      applySectionChange,
    });
  }

  addLayerTypeChangeWithSidebarAction({ prevLayerType, nextLayerType, prevSidebarSection, applyLayerChange }) {
    this._pushLayerTypeChangeWithSidebar({
      prevLayerType,
      nextLayerType,
      prevSidebarSection,
      applyLayerChange,
    });
  }

  addMultiDeleteAction({ objectsData, refreshCallback, topAndBottomObjectAvailable, filterTuftData, filteredObjectNames }) {
    this._pushMultiDelete({
      filteredObjectNames,
      objectsData,
      refreshCallback,
      topAndBottomObjectAvailable,
      filterTuftData,
    });
  }

  // Keep your existing methods - just uncomment the Unity calls
  async undoMirrorObject({ objectName, componentType, originalData, newlyCreatedObjects, type, refreshCallback }) {
    const selectedHeight = selectSelectedHeight(getAppState());
    const payload = {
      propInfoCommandDatas: [
        {
          objectName: objectName,
        },
      ],
      productBuilderModel: {height: selectedHeight?.value || "12"},
    };
    disableGameObject(payload);
    // Small delay to ensure Unity processes
    await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
               
    // await new Promise((resolve) => setTimeout(resolve, 50));
    refreshCallback?.();
    await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
  }

  async redoMirrorObject({ objectName, componentType, originalData, newlyCreatedObjects, type, refreshCallback }) {
    const selectedHeight = selectSelectedHeight(getAppState());
    const payload = {
      propInfoCommandDatas: [
        {
          objectName: objectName,
        },
      ],
      productBuilderModel: {
        operationType: 'AutomaticPlacement',
        objectNameToModify: originalData?.objectName,
        placementType: 'Mirror',
        height: selectedHeight?.value || "12"
      },
    };
    enableGameObject(payload);
    // Small delay to ensure Unity processes
    // await new Promise((resolve) => setTimeout(resolve, 50));
    await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
    refreshCallback?.();
    await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
  }

  async undoAdjacentObject({
    objectName,
    componentType,
    originalData,
    newlyCreatedObjects,
    type,
    refreshCallback,
  }) {
    const selectedHeight = selectSelectedHeight(getAppState());
    const payload = {
      propInfoCommandDatas: [
        {
          objectName: objectName,
        },
      ],
      productBuilderModel: {height: selectedHeight?.value || "12"},
    };
    disableGameObject(payload);
    await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
               
    // Small delay to ensure Unity processes
    // await new Promise((resolve) => setTimeout(resolve, 50));
    refreshCallback?.();
    await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
  }

  async redoAdjacentObject({
    objectName,
    componentType,
    originalData,
    newlyCreatedObjects,
    type,
    refreshCallback,
  }) {
    const selectedHeight = selectSelectedHeight(getAppState());
    const payload = {
      propInfoCommandDatas: [
        {
          objectName: objectName,
        },
      ],
      productBuilderModel: {
        operationType: 'AutomaticPlacement',
        objectNameToModify: originalData?.objectName,
        placementType: 'Adjacent',
        height: selectedHeight?.value || "12"
      },
    };
    enableGameObject(payload);
    // Small delay to ensure Unity processes
    // await new Promise((resolve) => setTimeout(resolve, 50));
    await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
    refreshCallback?.();
    await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
  }
}
